import { Box, Checkbox, CircularProgress, FormControlLabel, Popover } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useV2Theme } from "../../hooks/useV2Theme";
import { useCreateBulkExport } from "../../../hooks/useBulkExport";
import { BulkExportData } from "../../../types/interaction/BulkExport";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BulkExportPopoverProps {
  readonly open: boolean;
  readonly anchorEl: HTMLElement | null;
  readonly onClose: () => void;
  readonly customerId: string;
  readonly selectedIds: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DATA_TYPES: { value: BulkExportData; labelKey: string }[] = [
  { value: BulkExportData.Metadata, labelKey: "metadata" },
  { value: BulkExportData.Media, labelKey: "media" },
  { value: BulkExportData.Transcript, labelKey: "transcript" },
];

const MAX_EXPORT_COUNT = 100;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkExportPopover({ // NOSONAR
  open,
  anchorEl,
  onClose,
  customerId,
  selectedIds,
}: BulkExportPopoverProps) {
  const { t } = useTranslation(["common", "errors"]);
  const { mode } = useV2Theme();
  const navigate = useNavigate();
  const isDark = mode === "dark";

  const createExport = useCreateBulkExport();

  const [checkedTypes, setCheckedTypes] = useState<Set<BulkExportData>>(
    new Set([BulkExportData.Metadata, BulkExportData.Media, BulkExportData.Transcript])
  );

  const [success, setSuccess] = useState(false);

  const toggleType = (type: BulkExportData) => {
    setCheckedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const count = selectedIds.length;
  const tooMany = count > MAX_EXPORT_COUNT;
  const tooFew = count < 2;
  const noTypes = checkedTypes.size === 0;
  const canSubmit = !tooMany && !tooFew && !noTypes && !createExport.isPending;

  const handleExport = async () => {
    if (!canSubmit) return;

    try {
      await createExport.mutateAsync({
        customerId,
        interactionIds: selectedIds,
        dataToExport: Array.from(checkedTypes),
      });
      setSuccess(true);
    } catch (err) {
      console.error("Failed to create bulk export:", err);
    }
  };

  const handleGoToJobs = () => {
    onClose();
    setSuccess(false);
    navigate(`/customers/${customerId}/v2/jobs/bulk-export`);
  };

  const handleClose = () => {
    onClose();
    // Reset state after close animation
    setTimeout(() => {
      setSuccess(false);
      setCheckedTypes(
        new Set([BulkExportData.Metadata, BulkExportData.Media, BulkExportData.Transcript])
      );
    }, 200);
  };

  // -- Hardcoded theme colors for portal-rendered popover --------------------
  const bg = isDark ? "#1A2332" : "#FFFFFF";
  const text = isDark ? "#F1F5F9" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.12)";
  const accent = isDark ? "#06D6A0" : "#059669";
  const errorColor = isDark ? "#FB7185" : "#E11D48";
  const inputBorder = isDark ? "rgba(148,163,184,0.25)" : "rgba(15,23,42,0.18)";

  const submitBgColor = canSubmit ? accent : (isDark ? "#253347" : "#E2E8F0");
  const submitTextColor = canSubmit ? (isDark ? "#0B1120" : "#FFFFFF") : textSecondary;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: bg,
            border: `1px solid ${border}`,
            borderRadius: "10px",
            boxShadow: isDark
              ? "0 8px 32px rgba(0,0,0,0.6)"
              : "0 8px 32px rgba(0,0,0,0.12)",
            color: text,
            mt: "4px",
            width: 320,
          },
        },
      }}
    >
      <Box sx={{ p: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
        {success ? (
          /* ---- Success state ---- */
          <>
            <Box sx={{ fontSize: "0.875rem", fontWeight: 700, color: accent }}>
              {t("common:exportStarted")}
            </Box>
            <Box sx={{ fontSize: "0.8125rem", color: textSecondary }}>
              {t("common:exportStartedDescription")}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", pt: "4px" }}>
              <Box
                component="button"
                onClick={handleClose}
                sx={{
                  px: "14px",
                  py: "7px",
                  borderRadius: "7px",
                  border: `1px solid ${inputBorder}`,
                  backgroundColor: "transparent",
                  color: textSecondary,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(148,163,184,0.08)"
                      : "rgba(15,23,42,0.04)",
                  },
                }}
              >
                {t("common:close")}
              </Box>
              <Box
                component="button"
                onClick={handleGoToJobs}
                sx={{
                  px: "14px",
                  py: "7px",
                  borderRadius: "7px",
                  border: "none",
                  backgroundColor: accent,
                  color: isDark ? "#0B1120" : "#FFFFFF",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.9 },
                }}
              >
                {t("common:viewExports")}
              </Box>
            </Box>
          </>
        ) : (
          /* ---- Form state ---- */
          <>
            <Box sx={{ fontSize: "0.875rem", fontWeight: 700, color: text }}>
              {t("common:exportInteractions", { count, plural: count === 1 ? "" : "s" })}
            </Box>

            {tooMany && (
              <Box sx={{ fontSize: "0.75rem", color: errorColor }}>
                {t("errors:tooManyInteractions", { max: MAX_EXPORT_COUNT })}
              </Box>
            )}

            {tooFew && (
              <Box sx={{ fontSize: "0.75rem", color: errorColor }}>
                {t("errors:selectAtLeastTwoInteractions")}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              <Box
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  color: textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: "4px",
                }}
              >
                {t("common:dataToExport")}
              </Box>
              {DATA_TYPES.map((dt) => (
                <FormControlLabel
                  key={dt.value}
                  control={
                    <Checkbox
                      checked={checkedTypes.has(dt.value)}
                      onChange={() => toggleType(dt.value)}
                      size="small"
                      sx={{
                        color: isDark ? "#64748B" : "#94A3B8",
                        "&.Mui-checked": { color: accent },
                      }}
                    />
                  }
                  label={t(`common:${dt.labelKey}`)}
                  sx={{
                    mx: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8125rem",
                      color: textSecondary,
                    },
                  }}
                />
              ))}
              {noTypes && (
                <Box sx={{ fontSize: "0.75rem", color: errorColor, mt: "2px" }}>
                  {t("errors:selectAtLeastOneDataType")}
                </Box>
              )}
            </Box>

            {createExport.isError && (
              <Box sx={{ fontSize: "0.75rem", color: errorColor }}>
                {t("errors:exportFailed")}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end", pt: "4px" }}>
              <Box
                component="button"
                onClick={handleClose}
                sx={{
                  px: "14px",
                  py: "7px",
                  borderRadius: "7px",
                  border: `1px solid ${inputBorder}`,
                  backgroundColor: "transparent",
                  color: textSecondary,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(148,163,184,0.08)"
                      : "rgba(15,23,42,0.04)",
                  },
                }}
              >
                {t("common:cancel")}
              </Box>
              <Box
                component="button"
                onClick={handleExport}
                disabled={!canSubmit}
                sx={{
                  px: "14px",
                  py: "7px",
                  borderRadius: "7px",
                  border: "none",
                  backgroundColor: submitBgColor,
                  color: submitTextColor,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: canSubmit ? "pointer" : "default",
                  opacity: canSubmit ? 1 : 0.6,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  "&:hover": canSubmit ? { opacity: 0.9 } : {},
                }}
              >
                {createExport.isPending && (
                  <CircularProgress
                    size={14}
                    sx={{ color: isDark ? "#0B1120" : "#FFFFFF" }}
                  />
                )}
                {t("common:bulkExport")}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
}
